import { Button, Card, Divider, Flex } from 'antd'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  ContentSpinner,
  QueryErrorAlert,
  RevokeCredentialModal,
  RotateCredentialModal
} from 'src/components'
import {
  AppPermissionPanel,
  CredentialDetailToolbar,
  CredentialMetadataDescriptions,
  isCredentialLocked,
  isCredentialRevoked,
  QuickStartCard,
  TokenRevealBanner,
  useCredentialTokenReveal
} from 'src/features/workspaces/components/credentials'
import {
  WorkspacePageShell,
  useWorkspaceRoute
} from 'src/features/workspaces/pages/WorkspacePageShell'
import { paths } from 'src/router/paths'
import { useShowApiErrors } from 'src/utils'

import {
  AppCredentialEditDrawer,
  type EditFormValues
} from '../../components/AppCredentialEditDrawer'
import { useWorkspaceAppCredential } from '../../hooks/useWorkspaceAppCredential'
import { useWorkspaceAppDetail } from '../../hooks/useWorkspaceAppDetail'
import { useWorkspaceAppsList } from '../../hooks/useWorkspaceAppsList'

type Action = { kind: 'edit' } | { kind: 'rotate' } | { kind: 'revoke' } | null

/**
 * Normalises an app-provided URL for the curl sample. Prepends `https://`
 * only when the value doesn't already carry a scheme, so `app.domain`
 * arriving as either `api.crm.skalx.dev` or `https://api.crm.skalx.dev`
 * both render correctly.
 */
const resolveAppEndpoint = (
  value: string | null | undefined
): string | null => {
  if (!value) return null

  return /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`
}

export const WorkspaceAppCredentialDetailPage = () => {
  const { t } = useTranslation()
  const showApiErrors = useShowApiErrors()
  const { id, workspaceId, idValid } = useWorkspaceRoute()
  const { appId = '', credentialId = '' } = useParams<{
    appId: string
    credentialId: string
  }>()

  const applicationId = Number(appId) || 0
  const navigate = useNavigate()

  const { apps } = useWorkspaceAppsList(idValid ? workspaceId : 0)
  const app = apps.find((a) => a.id === appId)
  // Built-in apps expose `domain`, third-party custom apps expose `appUrl`.
  // Either can arrive with or without a scheme (backend is inconsistent), so
  // `resolveAppEndpoint` normalises. Public API docs live at `<base>/public/docs`.
  const appEndpoint = resolveAppEndpoint(app?.domain ?? app?.appUrl ?? null)
  const appDocsUrl = appEndpoint
    ? `${appEndpoint.replace(/\/+$/, '')}/public/docs`
    : null

  const {
    data: credential,
    isLoading,
    isError,
    refetch
  } = useWorkspaceAppCredential(applicationId, credentialId)

  const detail = useWorkspaceAppDetail(workspaceId, appId)

  const [action, setAction] = useState<Action>(null)
  const closeAction = () => setAction(null)

  const { revealedToken, setRevealedToken, clearRevealed } =
    useCredentialTokenReveal()

  if (!idValid || !appId || !credentialId || !applicationId) {
    return <Navigate to={paths.workspaces.apps.root(id)} replace />
  }

  const backButton = (
    <Link to={paths.workspaces.apps.credentials(id, appId)}>
      <Button size="small" icon={<ArrowLeft size={14} />} type="text">
        {t('workspace.apps.credentialDetail.back', { app: app?.name ?? '' })}
      </Button>
    </Link>
  )

  // Breadcrumb chain: Apps → <app name> → Credentials → <credential> so a
  // user landing on a deep URL still sees the full location and can climb.
  const crumbs = [
    { title: t('shell.menu.apps'), href: paths.workspaces.apps.root(id) },
    ...(app
      ? [{ title: app.name, href: paths.workspaces.apps.details(id, appId) }]
      : []),
    {
      title: t('workspace.apps.tabs.credentials'),
      href: paths.workspaces.apps.credentials(id, appId)
    }
  ]

  if (isLoading) {
    return (
      <WorkspacePageShell
        breadcrumbExtras={crumbs}
        breadcrumbLeaf={t('workspace.apps.credentialDetail.breadcrumb')}
      >
        {backButton}
        <ContentSpinner minHeight={240} />
      </WorkspacePageShell>
    )
  }

  if (isError || !credential) {
    return (
      <WorkspacePageShell
        breadcrumbExtras={crumbs}
        breadcrumbLeaf={t('workspace.apps.credentialDetail.breadcrumb')}
      >
        <Flex vertical gap={14}>
          {backButton}
          <QueryErrorAlert onRetry={() => void refetch()} />
        </Flex>
      </WorkspacePageShell>
    )
  }

  const locked = isCredentialLocked(credential.status)
  const revoked = isCredentialRevoked(credential.status)

  const handleEdit = async (values: EditFormValues) => {
    try {
      await detail.updateCredential({
        path: { application_id: applicationId, id: credential.id },
        body: {
          name: values.name,
          expires_at: values.expiresAt ? values.expiresAt.toISOString() : null,
          permission_codes: values.permission_codes
        }
      })
      closeAction()
    } catch (err) {
      showApiErrors(err)
    }
  }

  const handleRotate = async (expiresAt: string | null, revokeOld: boolean) => {
    try {
      const result = await detail.rotateCredential({
        path: { application_id: applicationId, id: credential.id },
        body: { expires_at: expiresAt, revoke_old_credential: revokeOld }
      })
      closeAction()
      setRevealedToken(result.new_credential.token)
    } catch (err) {
      showApiErrors(err)
    }
  }

  const handleRevoke = async (reason: string | null) => {
    try {
      await detail.revokeCredential({
        path: { application_id: applicationId, id: credential.id },
        body: { reason }
      })
      navigate(paths.workspaces.apps.credentials(id, appId))
    } catch (err) {
      showApiErrors(err)
    }
  }

  return (
    <WorkspacePageShell
      breadcrumbExtras={crumbs}
      breadcrumbLeaf={credential.name}
    >
      <Flex vertical gap={16}>
        <CredentialDetailToolbar
          backTo={paths.workspaces.apps.credentials(id, appId)}
          backLabel={t('workspace.apps.credentialDetail.back', {
            app: app?.name ?? ''
          })}
          locked={locked}
          revoked={revoked}
          onEdit={() => setAction({ kind: 'edit' })}
          onRotate={() => setAction({ kind: 'rotate' })}
          onRevoke={() => setAction({ kind: 'revoke' })}
          rotateLabel={t('workspace.apps.credentials.rotate')}
          revokeLabel={t('workspace.apps.credentials.revoke')}
        />

        {revealedToken ? (
          <TokenRevealBanner token={revealedToken} onDismiss={clearRevealed} />
        ) : null}

        <Card title={t('workspace.apps.credentialDetail.overviewTitle')}>
          <CredentialMetadataDescriptions credential={credential} />

          <Divider style={{ marginBlock: 20 }} />

          <AppPermissionPanel permissionCodes={credential.permission_codes} />
        </Card>

        <QuickStartCard
          token={revealedToken}
          endpoint={appEndpoint}
          docsUrl={appDocsUrl}
        />
      </Flex>

      <AppCredentialEditDrawer
        open={action?.kind === 'edit'}
        app={app}
        credential={credential}
        submitting={detail.isUpdatingCredential}
        onSubmit={handleEdit}
        onClose={closeAction}
      />

      <RotateCredentialModal
        open={action?.kind === 'rotate'}
        title={t('workspace.apps.credentials.rotateConfirmTitle')}
        description={t('workspace.apps.credentials.rotateConfirmBody')}
        okText={t('workspace.apps.credentials.rotate')}
        loading={detail.isRotatingCredential}
        onConfirm={handleRotate}
        onCancel={closeAction}
      />

      <RevokeCredentialModal
        open={action?.kind === 'revoke'}
        title={t('workspace.apps.credentials.revokeConfirmTitle')}
        description={t('workspace.apps.credentials.revokeConfirmBody')}
        okText={t('workspace.apps.credentials.revoke')}
        loading={detail.isRevokingCredential}
        onConfirm={handleRevoke}
        onCancel={closeAction}
      />
    </WorkspacePageShell>
  )
}
